import React from 'react';
import { UnifiedSessionData } from '../types/DataTypes';
import { getHiddenMetrics } from '../utils/sourceDataCapabilities';

interface PostCompletionMetricsGridProps {
  session: UnifiedSessionData;
  layout: 'cardio' | 'interval' | 'strength' | 'hiit' | 'mobility' | 'workout';
  /**
   * Apr 2026 — LOAD_AWARE_ADAPTIVE_COACHING P4 §8.29 (web parity, 2026-05-19).
   *
   * Optional load_card payload. When present (and ``display_mode !== 'off'``),
   * adds one tile to the grid using the same two-line format as the native
   * version (see P3 — Legible Load Grove sprint). Web consumers do not yet
   * fetch this payload from `pages/WorkoutDetail.tsx`; this prop exists to
   * keep the contract aligned with the native grid so wiring it up later is
   * a single-call change. See native: `PostCompletionMetricsGrid.native.tsx`.
   */
  loadPayload?: {
    display_mode: 'full' | 'simple' | 'therapeutic' | 'off';
    alternate_label?: string;
    class?: string | null;
    class_label?: string | null;
    normalized_load?: number | null;
    is_estimated?: boolean;
  } | null;
  /** Tap handler for the Load tile — caller scrolls to / expands the
   *  ExpandableLoadAndRecovery card. Optional. */
  onLoadTilePress?: () => void;
}

export const PostCompletionMetricsGrid: React.FC<PostCompletionMetricsGridProps> = ({
  session,
  layout,
  loadPayload,
  onLoadTilePress,
}) => {
  const metrics: Array<{
    label: string;
    value: string | number;
    icon: string;
    priority: number; // Lower is higher priority
    caption?: string; // Optional small footer line (e.g., "Garmin · Apr 26")
    /** Tap handler for tiles that open a detail view (today: Load tile). */
    onClick?: () => void;
  }> = [];

  // Format an ISO timestamp like "2026-04-26T12:00:00+00:00" → "Apr 26".
  const formatAsOf = (iso: string | null | undefined): string => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  // Pretty-print the resolver's source enum for the tile caption.
  const formatVO2Source = (source: string | undefined): string => {
    switch (source) {
      case 'garmin': return 'Garmin';
      case 'apple_health': return 'Apple Health';
      case 'health_connect': return 'Health Connect';
      case 'fitbit': return 'Fitbit';
      case 'ewma_per_activity': return 'Cedar (avg of recent runs)';
      default: return '';
    }
  };

  // Helper to format pace
  const formatPace = (paceSeconds: number): string => {
    const minutes = Math.floor(paceSeconds / 60);
    const seconds = Math.floor(paceSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
  };

  // Helper to format duration
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Normalize 'workout' layout to 'cardio' so generic/ad-hoc activities get full metrics
  const effectiveLayout = layout === 'workout' ? 'cardio' : layout;

  // UNIVERSAL METRICS (all layouts)
  // Duration - Priority 1
  // Primary: totalDuration (seconds). Fallback: duration alias from some API responses.
  const durationSeconds =
    (session.totalDuration && session.totalDuration > 0 ? session.totalDuration : 0) ||
    (session.duration && session.duration > 0 ? session.duration : 0);
  if (durationSeconds > 0) {
    metrics.push({
      label: 'Duration',
      value: formatDuration(durationSeconds / 60), // Convert seconds to minutes
      icon: '',
      priority: 1
    });
  }

  // CARDIO LAYOUT
  if (effectiveLayout === 'cardio') {
    if (session.distance && session.distance > 0) {
      metrics.push({
        label: 'Distance',
        value: `${(session.distance / 1000).toFixed(2)} km`,
        icon: '',
        priority: 2
      });
    }
    if (session.avgPace && session.avgPace > 0) {
      metrics.push({
        label: 'Avg Pace',
        value: formatPace(session.avgPace),
        icon: '',
        priority: 3
      });
    }
    if (session.elevationGain && session.elevationGain > 0) {
      metrics.push({
        label: 'Elevation',
        value: `${Math.round(session.elevationGain)}m`,
        icon: '',
        priority: 4
      });
    }
    if (session.avgHeartRate && session.avgHeartRate > 0) {
      metrics.push({
        label: 'Avg HR',
        value: `${Math.round(session.avgHeartRate)} bpm`,
        icon: '',
        priority: 5
      });
    }
    if (session.calories && session.calories > 0) {
      metrics.push({
        label: 'Calories',
        value: `${Math.round(session.calories)} kcal`,
        icon: '',
        priority: 6
      });
    }
  }

  // INTERVAL LAYOUT
  if (effectiveLayout === 'interval') {
    if (session.distance && session.distance > 0) {
      metrics.push({
        label: 'Distance',
        value: `${(session.distance / 1000).toFixed(2)} km`,
        icon: '',
        priority: 2
      });
    }
    if (session.avgPace && session.avgPace > 0) {
      metrics.push({
        label: 'Avg Pace',
        value: formatPace(session.avgPace),
        icon: '',
        priority: 3
      });
    }
    if (session.phases && session.phases.length > 0) {
      const completedPhases = session.phases.filter(p => p.actualDuration && p.prescribedDuration && p.actualDuration >= p.prescribedDuration * 0.9).length;
      metrics.push({
        label: 'Phases',
        value: `${completedPhases}/${session.phases.length}`,
        icon: '',
        priority: 4
      });
    }
    if (session.avgHeartRate && session.avgHeartRate > 0) {
      metrics.push({
        label: 'Avg HR',
        value: `${Math.round(session.avgHeartRate)} bpm`,
        icon: '',
        priority: 5
      });
    }
    if (session.elevationGain && session.elevationGain > 0) {
      metrics.push({
        label: 'Elevation',
        value: `${Math.round(session.elevationGain)}m`,
        icon: '',
        priority: 6
      });
    }
  }

  // STRENGTH LAYOUT
  // Spec (March 2026): No Volume. No Avg Weight.
  // Grid: Duration, Exercises (n/n), Calories, Sets Done (n/n), Avg HR
  if (effectiveLayout === 'strength') {
    // Exercises Completed - Priority 2
    if (session.exercises && session.exercises.length > 0) {
      const completedExercises = session.exercises.filter(e => e.completed).length;
      metrics.push({
        label: 'Exercises',
        value: `${completedExercises}/${session.exercises.length}`,
        icon: '',
        priority: 2
      });
    }

    // Calories - Priority 3
    if (session.calories && session.calories > 0) {
      metrics.push({
        label: 'Calories',
        value: `${Math.round(session.calories)} kcal`,
        icon: '',
        priority: 3
      });
    }

    // Sets Completed - Priority 4
    if (session.exercises) {
      const totalSets = session.exercises.reduce((sum, ex) => sum + (ex.actualSets || 0), 0);
      if (totalSets > 0) {
        metrics.push({
          label: 'Sets Done',
          value: totalSets,
          icon: '',
          priority: 4
        });
      }
    }

    // Avg HR - Priority 5
    if (session.avgHeartRate && session.avgHeartRate > 0) {
      metrics.push({
        label: 'Avg HR',
        value: `${Math.round(session.avgHeartRate)} bpm`,
        icon: '',
        priority: 5
      });
    }
  }

  // HIIT LAYOUT
  if (effectiveLayout === 'hiit') {
    // Rounds/Minutes Completed - Priority 2
    if (session.hiitType && (session.circuits || session.emom || session.amrap)) {
      const type = session.hiitType;
      let roundsCompleted: number | undefined;
      let minutesCompleted: number | undefined;
      let roundsTarget: number | undefined;
      let timeTarget: number | undefined;
      
      if (type === 'circuit' && session.circuits) {
        roundsCompleted = session.circuits.reduce((sum, c) => sum + (c.completedRounds ?? 0), 0);
        roundsTarget = session.circuits.reduce((sum, c) => sum + (c.totalRounds ?? 0), 0) || undefined;
      } else if (type === 'emom' && session.emom) {
        minutesCompleted = session.emom.completedMinutes ?? 0;
        timeTarget = session.emom.totalMinutes;
      } else if (type === 'amrap' && session.amrap) {
        roundsCompleted = session.amrap.roundsCompleted ?? 0;
      }
      
      if (type === 'circuit' && roundsCompleted !== undefined) {
        metrics.push({
          label: 'Rounds',
          value: roundsTarget ? `${roundsCompleted}/${roundsTarget}` : roundsCompleted,
          icon: '',
          priority: 2
        });
      } else if (type === 'emom' && minutesCompleted !== undefined) {
        metrics.push({
          label: 'Minutes',
          value: timeTarget ? `${minutesCompleted}/${timeTarget}` : minutesCompleted,
          icon: '',
          priority: 2
        });
      } else if (type === 'amrap' && roundsCompleted !== undefined) {
        metrics.push({
          label: 'Rounds',
          value: roundsCompleted,
          icon: '',
          priority: 2
        });
      }
    }

    // Exercises Completed - Priority 3
    if (session.exercises && session.exercises.length > 0) {
      const completedExercises = session.exercises.filter(e => e.completed).length;
      metrics.push({
        label: 'Exercises',
        value: `${completedExercises}/${session.exercises.length}`,
        icon: '',
        priority: 3
      });
    }

    // Heart Rate - Priority 4
    if (session.avgHeartRate && session.avgHeartRate > 0) {
      metrics.push({
        label: 'Avg HR',
        value: `${Math.round(session.avgHeartRate)} bpm`,
        icon: '',
        priority: 4
      });
    }

    // Calories - Priority 5
    if (session.calories && session.calories > 0) {
      metrics.push({
        label: 'Calories',
        value: `${Math.round(session.calories)} kcal`,
        icon: '',
        priority: 5
      });
    }
  }

  // MOBILITY LAYOUT
  if (effectiveLayout === 'mobility') {
    // Movements Completed - Priority 2
    if (session.exercises && session.exercises.length > 0) {
      const completedMovements = session.exercises.filter(e => e.completed).length;
      metrics.push({
        label: 'Movements',
        value: `${completedMovements}/${session.exercises.length}`,
        icon: '',
        priority: 2
      });
    }

    // Exercises - Priority 3
    const exerciseCount = session.exercises?.filter(e => e.exerciseName).length ?? 0;
    if (exerciseCount > 0) {
      metrics.push({
        label: 'Exercises',
        value: exerciseCount,
        icon: '',
        priority: 3
      });
    }
    // Duration already added universally above — no duplicate needed here
  }

  // ---- Row 3: Advanced metrics (cadence, power, training effect, VO2) ----
  // These show for ANY layout when the data is present — they're device-sourced metrics.
  if (session.avgCadence && session.avgCadence > 0) {
    const cadenceUnit = effectiveLayout === 'cardio' ? 'spm' : 'rpm';
    metrics.push({
      label: 'Avg Cadence',
      value: `${Math.round(session.avgCadence)} ${cadenceUnit}`,
      icon: '',
      priority: 10
    });
  }
  if (session.avgPower && session.avgPower > 0) {
    metrics.push({
      label: 'Avg Power',
      value: `${Math.round(session.avgPower)} W`,
      icon: '',
      priority: 11
    });
  }
  if (session.trainingEffect && session.trainingEffect > 0) {
    metrics.push({
      label: 'Training Effect',
      value: session.trainingEffect.toFixed(1),
      icon: '📈',
      priority: 12
    });
  }

  // ======================================================================
  // Apr 2026 — LOAD_AWARE_ADAPTIVE_COACHING P4 §8.29 (web parity)
  // Legible Load Grove sprint P3.1 (2026-05-19): two-line format.
  // Big value (top, single token) + label line (bottom, holds the "·"
  // separator + qualifier). Matches native at
  // `PostCompletionMetricsGrid.native.tsx`. Modes:
  //   full        → value "73"        | label "Load · Moderate"
  //   simple      → value "Moderate"  | label "Load"
  //   therapeutic → value "Light"     | label "Exertion"
  //   off         → tile not rendered (this branch returns early)
  //
  // TODO(visual-regression): web has no snapshot harness for this grid
  // yet. When one is added, capture all 4 display modes per P3.2 audit.
  // ======================================================================
  if (
    loadPayload
    && loadPayload.display_mode
    && loadPayload.display_mode !== 'off'
    && (loadPayload.class_label || loadPayload.normalized_load != null)
  ) {
    const mode = loadPayload.display_mode;
    const isTherapeutic = mode === 'therapeutic';
    const classWord = loadPayload.class_label
      ? loadPayload.class_label.charAt(0).toUpperCase() + loadPayload.class_label.slice(1)
      : '';
    const numericLoad =
      !isTherapeutic && loadPayload.normalized_load != null
        ? Math.round(loadPayload.normalized_load as number)
        : null;

    let tileValue: string;
    let tileLabel: string;
    if (isTherapeutic) {
      tileValue = classWord || '—';
      tileLabel = 'Exertion';
    } else if (mode === 'simple') {
      tileValue = classWord || '—';
      tileLabel = 'Load';
    } else {
      // 'full' (default for performance users)
      if (numericLoad != null) {
        tileValue = `${numericLoad}`;
        tileLabel = classWord ? `Load · ${classWord}` : 'Load';
      } else {
        tileValue = classWord || '—';
        tileLabel = 'Load';
      }
    }

    metrics.push({
      label: tileLabel,
      value: tileValue,
      icon: '',
      priority: 11.5,
      onClick: onLoadTilePress,
    });
  }
  // Current VO2 max — resolver-derived rolling value (Garmin > Apple > HC > Fitbit
  // > 30d EWMA fallback). Replaces the noisy per-activity vo2_max_estimate that
  // used to sit here. April 2026 device-import parity mission.
  if (session.currentVo2Max?.value != null && session.currentVo2Max.value > 0) {
    const sourceLabel = formatVO2Source(session.currentVo2Max.source);
    const dateLabel = formatAsOf(session.currentVo2Max.as_of);
    metrics.push({
      label: 'Current VO2 max',
      value: `${Math.round(session.currentVo2Max.value)}`,
      icon: '',
      priority: 13,
      caption: sourceLabel && dateLabel ? `${sourceLabel} · ${dateLabel}` : sourceLabel,
    });
  }

  // ---- Row 4: Running dynamics (Garmin-specific) ----
  if (session.groundContactTimeMs && session.groundContactTimeMs > 0) {
    metrics.push({
      label: 'GCT',
      value: `${Math.round(session.groundContactTimeMs)} ms`,
      icon: '',
      priority: 20
    });
  }
  if (session.verticalOscillationCm && session.verticalOscillationCm > 0) {
    metrics.push({
      label: 'Vert. Osc.',
      value: `${session.verticalOscillationCm.toFixed(1)} cm`,
      icon: '',
      priority: 21
    });
  }
  if (session.strideLengthM && session.strideLengthM > 0) {
    metrics.push({
      label: 'Stride',
      value: `${session.strideLengthM.toFixed(2)} m`,
      icon: '',
      priority: 22
    });
  }

  // P3-18: Filter out metrics the source can't provide (parity with native grid)
  const hidden = getHiddenMetrics(
    session.dataSource ?? session.source,
    session.sourceAppName,
  );
  const filteredMetrics = metrics.filter(m => !hidden.has(m.label));

  // Sort by priority — no hard cap. Show all available metrics (parity with native grid).
  const sortedMetrics = filteredMetrics
    .sort((a, b) => a.priority - b.priority);

  // Don't render if no metrics
  if (sortedMetrics.length === 0) {
    return null;
  }

  // P3-18: Adaptive column count based on metric count
  // 1-2 metrics → 2 columns (dignified minimal), 3+ → 3 columns (standard)
  const gridCols = sortedMetrics.length <= 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className="mx-4 mt-4 mb-2">
      <div className={`grid ${gridCols} gap-2`}>
        {sortedMetrics.map((metric, index) => {
          // Tiles with `onClick` (today: Load tile) render as a button so
          // tap-to-scroll-to-drawer works on web with keyboard + a11y
          // semantics. Other tiles remain static divs.
          const baseClass = 'rounded-xl py-2.5 px-2 text-center';
          const bgStyle = { backgroundColor: 'var(--cedar-subtle, #E8F2ED)' };
          const labelEl = (
            <div
              className="text-[11px] font-medium mb-1 tracking-wide"
              style={{ color: 'var(--coloron-surface600, rgba(17, 60, 43, 0.72))' }}
            >
              {metric.label}
            </div>
          );
          const valueEl = (
            <div
              className="text-base font-bold"
              style={{ color: 'var(--coloron-surface900, #113C2B)' }}
            >
              {metric.value}
            </div>
          );
          const captionEl = metric.caption ? (
            <div
              className="text-[10px] font-normal mt-0.5"
              style={{ color: 'var(--coloron-surface600, rgba(17, 60, 43, 0.62))' }}
            >
              {metric.caption}
            </div>
          ) : null;
          if (metric.onClick) {
            return (
              <button
                key={index}
                type="button"
                onClick={metric.onClick}
                className={`${baseClass} cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1`}
                style={bgStyle}
                aria-label={`${metric.label}: ${metric.value}. Tap for details.`}
              >
                {labelEl}
                {valueEl}
                {captionEl}
              </button>
            );
          }
          return (
            <div key={index} className={baseClass} style={bgStyle}>
              {labelEl}
              {valueEl}
              {captionEl}
            </div>
          );
        })}
      </div>
    </div>
  );
};

